/**
 * Top-N filtering for chart data — keep the top/bottom N rows by value,
 * optionally rolling the remainder into a single "Other" row.
 */

import type { TopNConfig } from '../types';
import { NON_MEASURE_ROW_KEYS } from './nativeDataTransforms';

/**
 * Apply Top N filtering to chart data, if enabled.
 *
 * @param chartData - Chart-ready rows (one value key plus `x`/`xValues`)
 * @param topN - Top-N configuration, or undefined/disabled to pass through
 * @returns A new array — the top/bottom N rows (optionally with a summed
 *   "Other" row appended), or the original `chartData` unchanged when
 *   Top N is not enabled or has no data key to sort on
 */
export function applyTopN(
  chartData: Record<string, unknown>[],
  topN: TopNConfig | undefined
): Record<string, unknown>[] {
  if (!topN?.enabled || chartData.length === 0) {
    return chartData;
  }

  const { n, direction, groupOthers } = topN;
  // Exclude non-measure row keys when picking the measure to sort on.
  const dataKey = Object.keys(chartData[0]).find(
    key => !NON_MEASURE_ROW_KEYS.has(key)
  );

  if (!dataKey) {
    return chartData;
  }

  // Sort data
  const sorted = [...chartData].sort((a, b) => {
    const aVal = parseFloat(a[dataKey as keyof typeof a] as string) || 0;
    const bVal = parseFloat(b[dataKey as keyof typeof b] as string) || 0;
    return direction === 'top' ? bVal - aVal : aVal - bVal;
  });

  // Take top/bottom N
  const topRows = sorted.slice(0, n);

  // Group others if enabled
  if (groupOthers && sorted.length > n) {
    const others = sorted.slice(n);
    const otherSum = others.reduce((sum, item) => {
      return (
        sum + (parseFloat(item[dataKey as keyof typeof item] as string) || 0)
      );
    }, 0);

    // The "Other" row is intentionally non-drillable: it has no __sourceRowIndex.
    topRows.push({
      x: 'Other',
      [dataKey]: otherSum,
    } as (typeof topRows)[0]);
  }

  return topRows;
}
